import { Check } from "lucide-react";
import type { PpmChecklistPhoto, PpmChecklistPhotoTaskKey } from "@workspace/api-client-react";
import { PpmPhotoSlot } from "./ppm-photo-slot";

interface PpmTaskRowProps {
  inspectionId: number;
  taskKey: PpmChecklistPhotoTaskKey;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  photosDisabled?: boolean;
  photos: PpmChecklistPhoto[];
}

export function PpmTaskRow({
  inspectionId,
  taskKey,
  label,
  checked,
  onChange,
  disabled,
  photosDisabled,
  photos
}: PpmTaskRowProps) {
  const beforePhoto = photos.find(p => p.taskKey === taskKey && p.stage === "before");
  const afterPhoto = photos.find(p => p.taskKey === taskKey && p.stage === "after");

  // Keep checkbox toggling completely separate from photo slots (do not wrap slots in <label>)
  return (
    <div className="flex flex-col gap-3 p-3 sm:p-4 border border-slate-200 rounded-xl bg-white shadow-sm transition-colors hover:border-slate-300 print:break-inside-avoid print:shadow-none print:border-slate-300">
      <label className={`flex items-start gap-3 cursor-pointer ${disabled ? 'opacity-80' : ''}`}>
        <div className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${checked ? 'border-primary bg-primary' : 'border-slate-300 bg-white'}`}>
          {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
        </div>
        <span className="text-sm font-medium text-slate-800 leading-snug">{label}</span>
        <input 
          type="checkbox" 
          checked={checked} 
          onChange={e => onChange(e.target.checked)} 
          disabled={disabled} 
          className="sr-only" 
        />
      </label>

      {/* Photo Slots - outside the label so clicks don't toggle the checkbox */}
      <div className="pl-8 grid grid-cols-2 gap-3 sm:gap-4 print:pl-0">
        <PpmPhotoSlot 
          inspectionId={inspectionId}
          taskKey={taskKey}
          stage="before"
          photo={beforePhoto}
          isReadOnly={photosDisabled ?? !!disabled}
          taskLabel={label}
        />
        <PpmPhotoSlot 
          inspectionId={inspectionId}
          taskKey={taskKey}
          stage="after"
          photo={afterPhoto}
          isReadOnly={photosDisabled ?? !!disabled}
          taskLabel={label}
        />
      </div>
    </div>
  );
}
